/**
 * Editing a document's text in place (the workbench's Document view): what a
 * person can click to edit, and the edits they have made but not saved.
 *
 * An edit names its paragraph as the backend's reader does — a block's
 * `(paraId, occurrence, pos)`, or a table block and a cell — and carries the
 * text it replaces, taken from the parsed model (never the DOM), so a stale
 * edit is refused rather than misapplied (vNext-api `docs/workbench.md`).
 */

import type { BlockModel, DocxModel, InlineSegment, TableCellModel } from '@/lib/ooxml';

export interface EditTarget {
  /** Stable while the document is the same: block, cell and paragraph. */
  key: string;
  /** The paragraph, or the cell, the edit is in (a cell's paragraphs share it). */
  place: string;
  anchor: { para_id: string | null; occurrence: number; pos: number | null };
  cell: { row: number; col: number } | null;
  /** The text the edit replaces. */
  expected: string;
}

export interface PendingEdit {
  target: EditTarget;
  text: string;
}

export type PendingEdits = ReadonlyMap<string, PendingEdit>;

/** What `DocxContent` needs to offer editing; absent, nothing is editable. */
export interface DocxEditBinding {
  pending: PendingEdits;
  /** The target being edited now. */
  active: string | null;
  onActivate: (target: EditTarget) => void;
  onChange: (target: EditTarget, text: string) => void;
  onClose: () => void;
  /** A version of the document was read (the first, or a newer one). */
  onLoad?: (model: DocxModel) => void;
}

const EDITABLE_KINDS: ReadonlySet<BlockModel['kind']> = new Set([
  'paragraph',
  'heading',
  'list_item',
]);

/** Paragraphs, headings and list items outside text boxes. */
export function isEditableBlock(block: BlockModel): boolean {
  return EDITABLE_KINDS.has(block.kind) && !block.inTextbox;
}

function anchorOf(block: BlockModel): EditTarget['anchor'] {
  return { para_id: block.paraId, occurrence: block.occurrence, pos: block.pos };
}

/** A block's identity: its paragraph id where it has one (stable across
 * versions), else its position. */
function blockKey(block: BlockModel): string {
  return block.paraId ? `${block.paraId}.${block.occurrence}` : `@${block.pos}`;
}

export function paragraphTarget(block: BlockModel): EditTarget {
  const place = `p:${blockKey(block)}`;
  return {
    key: place,
    place,
    anchor: anchorOf(block),
    cell: null,
    expected: block.text,
  };
}

/** A paragraph of a table cell, by its text (an empty cell: `''`). */
export function cellTarget(
  block: BlockModel,
  row: number,
  col: number,
  index: number,
  text: string
): EditTarget {
  const place = `c:${blockKey(block)}:${row}:${col}`;
  return {
    key: `${place}:${index}`,
    place,
    anchor: anchorOf(block),
    cell: { row, col },
    expected: text,
  };
}

/**
 * A cell's paragraphs as edit targets, parallel to `cell.paragraphs`: null
 * where an edit can't address the entry — a text box's or a nested table's
 * text, or a paragraph with no text (an image alone). An empty cell is one
 * empty paragraph, to fill.
 */
export function cellTargets(
  block: BlockModel,
  row: number,
  col: number,
  cell: TableCellModel
): (EditTarget | null)[] {
  if (cell.paragraphs.length === 0) {
    return [cellTarget(block, row, col, 0, '')];
  }
  return cell.paragraphs.map((segments, i) => {
    const text = segmentsText(segments);
    return cell.ownParagraphs[i] && text !== '' ? cellTarget(block, row, col, i, text) : null;
  });
}

/** A paragraph's text as the reader sees it (tabs and breaks as characters). */
export function segmentsText(segments: InlineSegment[]): string {
  return segments
    .map((segment) => (segment.type === 'text' || segment.type === 'link' ? segment.text : ''))
    .join('');
}

/** Pending edits as the API takes them: each its paragraph, its cell, the
 * text it replaces and the new text, in a fixed order. */
export function editPayload(edits: PendingEdit[]) {
  return edits.map((edit) => ({
    anchor: edit.target.anchor,
    cell: edit.target.cell,
    expected: edit.target.expected,
    text: edit.text,
  }));
}

/** Every paragraph a person can edit in the document. */
export function editTargets(model: DocxModel): EditTarget[] {
  const targets: EditTarget[] = [];
  for (const block of model.blocks) {
    if (isEditableBlock(block)) {
      targets.push(paragraphTarget(block));
    } else if (block.kind === 'table' && block.table) {
      block.table.rows.forEach((row, r) =>
        row.forEach((cell, c) => {
          if (cell.isMaster) {
            for (const target of cellTargets(block, r, c, cell)) {
              if (target) {
                targets.push(target);
              }
            }
          }
        })
      );
    }
  }
  return targets;
}

/**
 * Pending edits against a newer version of the document: an edit is kept,
 * re-anchored, where its paragraph still reads as it did when the edit was
 * made (in a cell, any paragraph of the cell, as the backend matches them),
 * and dropped where it doesn't.
 */
export function recheckEdits(
  pending: PendingEdits,
  model: DocxModel
): { kept: Map<string, PendingEdit>; dropped: PendingEdit[] } {
  const byPlace = new Map<string, EditTarget[]>();
  for (const target of editTargets(model)) {
    byPlace.set(target.place, [...(byPlace.get(target.place) ?? []), target]);
  }
  const kept = new Map<string, PendingEdit>();
  const dropped: PendingEdit[] = [];
  for (const edit of pending.values()) {
    const target = (byPlace.get(edit.target.place) ?? []).find(
      (candidate) => candidate.expected === edit.target.expected && !kept.has(candidate.key)
    );
    if (target) {
      kept.set(target.key, { target, text: edit.text });
    } else {
      dropped.push(edit);
    }
  }
  return { kept, dropped };
}
